import React, { useState, useEffect, useRef } from 'react';
import { formatUsd } from '../../data/countries';
import apiClient from '../../utils/api';
import { loadStripeJs } from './DepositModal';

const CardOnFilePanel = ({ stripePublishableKey, onUpdated, addToast }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const mountRef = useRef(null);
  const elementsRef = useRef(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    apiClient.getFeeStatus().then((res) => setStatus(res.data)).catch(() => {});
  }, []);

  const startSaveCard = async () => {
    if (!stripePublishableKey) {
      addToast("Stripe is not configured on the server.", "error");
      return;
    }
    setBusy(true);
    try {
      const setupRes = await apiClient.createStripeSetupIntent();
      const StripeCtor = await loadStripeJs();
      const stripe = StripeCtor(stripePublishableKey);
      const elements = stripe.elements({ clientSecret: setupRes.data.clientSecret });
      elementsRef.current = elements;
      if (mountRef.current) mountRef.current.innerHTML = "";
      const paymentElement = elements.create("payment");
      paymentElement.mount(mountRef.current);
      setShowForm(true);
    } catch (e) {
      addToast(e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const confirmCard = async () => {
    setBusy(true);
    try {
      const StripeCtor = await loadStripeJs();
      const stripe = StripeCtor(stripePublishableKey);
      const { setupIntent, error } = await stripe.confirmSetup({
        elements: elementsRef.current,
        redirect: "if_required"
      });
      if (error) throw new Error(error.message);
      const pm = setupIntent?.payment_method;
      const pmId = typeof pm === "string" ? pm : pm?.id;
      if (!pmId) throw new Error("No payment method returned");
      await apiClient.saveStripePaymentMethod(pmId);
      addToast("Card saved for management fees and top-ups.");
      const feeRes = await apiClient.getFeeStatus();
      setStatus(feeRes.data);
      onUpdated?.();
      if (mountRef.current) mountRef.current.innerHTML = "";
      setShowForm(false);
    } catch (e) {
      addToast(e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="detail-card" style={{ marginTop: "1rem" }}>
      <h3 style={{ fontSize: "1rem", marginBottom: "0.75rem" }}>Payment method &amp; management fees</h3>
      <p style={{ fontSize: "0.85rem", color: "var(--text-light)", marginBottom: "0.75rem" }}>
        1% per year (0.25% each quarter) is collected from rental payouts, your wallet, or this card if needed.
      </p>
      {status && (
        <p style={{ fontSize: "0.9rem", marginBottom: "0.75rem" }}>
          {status.cardOnFile ? (
            <>Card on file: <strong>{status.cardBrand || "Card"} •••• {status.cardLast4}</strong></>
          ) : (
            <span style={{ color: "var(--warning)" }}>No card on file — add one to cover fees when your wallet is low.</span>
          )}
          {status.totalDueUsd > 0 && (
            <span style={{ display: "block", marginTop: "0.35rem" }}>
              Outstanding fees: <strong>{formatUsd(status.totalDueUsd)}</strong>
            </span>
          )}
        </p>
      )}
      <div ref={mountRef} style={{ marginBottom: "0.75rem", minHeight: showForm ? "120px" : 0 }} />
      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
        <button type="button" className="btn btn-outline btn-sm" disabled={busy || !stripePublishableKey} onClick={startSaveCard}>
          {busy ? "Loading…" : "Add / replace card"}
        </button>
        {showForm && (
          <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={confirmCard}>
            Save card
          </button>
        )}
      </div>
    </div>
  );
};

export default CardOnFilePanel;
